/**
 * Teams Hub Navigation Configuration
 * 
 * Defines navigation structure, module config, commands, and hub links.
 * Uses @quotech/ui for unified navigation across all Quotech hubs.
 */

import {
  LayoutDashboard,
  Hash,
  MessageSquare,
  Users,
  StickyNote,
  Megaphone,
  Settings,
  Plus,
  UserPlus,
  Briefcase,
  Calendar,
  FileText,
  BarChart,
} from 'lucide-react'
import type { NavItem, ExternalLink, CommandItem, ModuleConfig, HubId, QuickAddItem } from '@quotech/ui'
import { getExternalLinks } from '@quotech/ui'

// =============================================================================
// MODULE CONFIG
// =============================================================================

export const MODULE_CONFIG: ModuleConfig = {
  id: 'teams' as HubId,
  name: 'Teams Hub',
  icon: Users,
  accentColor: 'violet',
  accentGradient: 'from-violet-500 to-purple-600',
  baseUrl: 'https://teams.quotech.ai',
  // Accent styling classes
  accentBgClass: 'bg-violet-600',
  accentHoverClass: 'hover:bg-violet-700',
  accentTextClass: 'text-violet-600',
  accentBadgeBgClass: 'bg-violet-100',
  accentBadgeTextClass: 'text-violet-700',
}

// =============================================================================
// MAIN NAVIGATION (Core Features)
// =============================================================================

export const NAV_ITEMS: NavItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    href: '/dashboard',
    description: 'Activity feed, mentions, and overview',
  },
  {
    id: 'channels',
    label: 'Channels',
    icon: Hash,
    href: '/channels',
    description: 'Group discussions and job channels',
    badge: 0, // Dynamic unread count
  },
  {
    id: 'messages',
    label: 'Direct Messages',
    icon: MessageSquare,
    href: '/messages',
    description: 'Private conversations',
    badge: 0, // Dynamic unread count
  },
  {
    id: 'notes',
    label: 'Notes',
    icon: StickyNote,
    href: '/notes',
    description: 'Cross-hub notes with threading',
  },
  {
    id: 'people',
    label: 'People',
    icon: Users,
    href: '/people',
    description: 'Team directory with presence',
  },
  {
    id: 'announcements',
    label: 'Announcements',
    icon: Megaphone,
    href: '/announcements',
    description: 'Company-wide updates',
  },
]

// =============================================================================
// SECONDARY NAVIGATION (Tools)
// =============================================================================

export const SECONDARY_NAV_ITEMS: NavItem[] = [
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    href: '/settings',
  },
]

// =============================================================================
// EXTERNAL HUB LINKS
// =============================================================================

export const EXTERNAL_LINKS: ExternalLink[] = getExternalLinks('teams')

// =============================================================================
// QUICK ADD ITEMS (Header dropdown)
// =============================================================================

export const QUICK_ADD_ITEMS: QuickAddItem[] = [
  {
    id: 'new-channel',
    label: 'New Channel',
    icon: Hash,
    action: () => { window.location.href = '/channels/new' },
    shortcut: '⌘⇧C',
  },
  {
    id: 'new-dm',
    label: 'New Message',
    icon: MessageSquare,
    action: () => { window.location.href = '/messages/new' },
    shortcut: '⌘⇧M',
  },
  {
    id: 'new-note',
    label: 'New Note',
    icon: StickyNote,
    action: () => { window.location.href = '/notes/new' },
    shortcut: '⌘⇧N',
  },
  {
    id: 'new-announcement',
    label: 'New Announcement',
    icon: Megaphone,
    action: () => { window.location.href = '/announcements/new' },
  },
]

// =============================================================================
// COMMAND PALETTE COMMANDS
// =============================================================================

export const COMMANDS: CommandItem[] = [
  // Actions
  {
    id: 'new-channel',
    label: 'Create Channel',
    description: 'Start a new group discussion',
    icon: Plus,
    action: () => { window.location.href = '/channels/new' },
    keywords: ['create', 'channel', 'group', 'new'],
    category: 'action',
  },
  {
    id: 'new-dm',
    label: 'Start Conversation',
    description: 'Message someone directly',
    icon: MessageSquare,
    action: () => { window.location.href = '/messages/new' },
    keywords: ['dm', 'direct', 'message', 'chat'],
    category: 'action',
  },
  {
    id: 'new-note',
    label: 'Add Note',
    description: 'Create a new note',
    icon: StickyNote,
    action: () => { window.location.href = '/notes/new' },
    keywords: ['note', 'add', 'create'],
    category: 'action',
  },
  
  // Navigation
  {
    id: 'go-dashboard',
    label: 'Go to Dashboard',
    icon: LayoutDashboard,
    action: () => { window.location.href = '/dashboard' },
    keywords: ['navigate', 'home', 'dashboard', 'activity'],
    category: 'navigation',
  },
  {
    id: 'go-channels',
    label: 'Go to Channels',
    icon: Hash,
    action: () => { window.location.href = '/channels' },
    keywords: ['navigate', 'channels', 'groups'],
    category: 'navigation',
  },
  {
    id: 'go-messages',
    label: 'Go to Messages',
    icon: MessageSquare,
    action: () => { window.location.href = '/messages' },
    keywords: ['navigate', 'messages', 'dm', 'direct'],
    category: 'navigation',
  },
  {
    id: 'go-notes',
    label: 'Go to Notes',
    icon: StickyNote,
    action: () => { window.location.href = '/notes' },
    keywords: ['navigate', 'notes'],
    category: 'navigation',
  },
  {
    id: 'go-people',
    label: 'Go to People',
    icon: Users,
    action: () => { window.location.href = '/people' },
    keywords: ['navigate', 'people', 'team', 'directory'],
    category: 'navigation',
  },
  
  // Hub switching
  {
    id: 'switch-jobs',
    label: 'Switch to Jobs Hub',
    icon: Briefcase,
    action: () => { window.location.href = 'https://dashboard.quotech.ai' },
    keywords: ['hub', 'jobs', 'switch'],
    category: 'hub',
  },
  {
    id: 'switch-workforce',
    label: 'Switch to Workforce Hub',
    icon: Calendar,
    action: () => { window.location.href = 'https://workforce.quotech.ai' },
    keywords: ['hub', 'workforce', 'switch'],
    category: 'hub',
  },
  {
    id: 'switch-hr',
    label: 'Switch to HR Hub',
    icon: UserPlus,
    action: () => { window.location.href = 'https://hr.quotech.ai' },
    keywords: ['hub', 'hr', 'switch'],
    category: 'hub',
  },
]

// =============================================================================
// HUB LINKS (for Command Palette)
// =============================================================================

export const HUB_LINKS: ExternalLink[] = EXTERNAL_LINKS

// =============================================================================
// LEGACY EXPORTS (for backward compatibility)
// =============================================================================

export const moduleConfig = MODULE_CONFIG
export const navigationItems = NAV_ITEMS
